// components/editor/anteditor/anteditor.js
import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect, // ⬅️ added
} from "react";
import { useSelector } from "react-redux";
import sanitizeHtml from "sanitize-html";
import { createAntTextTools } from "./antTextTools";
import { createAntRichContent } from "./antRichContent";
import { createAntBlocks } from "./antBlocks";
import "./antBlocks.css";
import "./anteditor.scss";
import "../styles.css";
import "./antRichContent.css";

import { createPortal } from "react-dom";
import { createNewInboxTask } from "../../../firebase/firebase.utils";
import AntListens from "./plugins/antListens";
import ColorPalette from "../../colors/color-palette";

import {
  BsQuote,
  BsFileBreak,
  BsTable,
  BsCardImage,
  BsPlayBtn,
  BsEmojiSmile,
  BsPaintBucket,
} from "react-icons/bs";
import { AiOutlineHighlight } from "react-icons/ai";
import {
  TbBold,
  TbItalic,
  TbUnderline,
  TbStrikethrough,
  TbAlignLeft,
  TbAlignRight,
  TbAlignCenter,
  TbAlignJustified,
  TbIndentIncrease,
  TbIndentDecrease,
  TbList,
  TbListNumbers,
  TbListCheck,
  TbSquareToggleHorizontal,
  TbChevronDown,
  TbPlus,
} from "react-icons/tb";
import { TfiLink, TfiLayoutMediaRightAlt, TfiParagraph } from "react-icons/tfi";
import {
  HiOutlineArrowUturnLeft,
  HiOutlineArrowUturnRight,
  HiOutlineCodeBracket,
} from "react-icons/hi2";
import EmojiPicker from "../../emoji/emojiPicker";

export default function AntEditor({
  mainToolbarOn,
  floatingToolbarOn,
  placeholder,
  initialValue,
  setDocContent,
  setDocImgFile,
  setDocImgFileAltText,
  docImgFileAltText,
  handleUploadDocImage,
  editability,
  onOpenLink,
}) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const [showBlockOptions, setShowBlockOptions] = useState(false);
  const [showAlignOptions, setShowAlignOptions] = useState(false);
  const [showInsertOptions, setShowInsertOptions] = useState(false);
  const [showTextColors, setShowTextColors] = useState(false);
  const [showHighlightColors, setShowHighlightColors] = useState(false);
  const [showFloatingToolbar, setShowFloatingToolbar] = useState(false);
  const [emisOpen, setEmIsOpen] = useState(false);
  const [spokenText, setSpokenText] = useState("");
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [showFileImgModal, setShowFileImgModal] = useState(false);
  const [insertingImage, setInsertingImage] = useState(false);
  const imageUploadBusy = useRef(false);
  const [showLinkImgModal, setShowLinkImgModal] = useState(false);
  const [imgFromWebLink, setImgFromWebLink] = useState(null);
  // New modal states for table and embed insertion.
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableRows, setTableRows] = useState(1);
  const [tableCols, setTableCols] = useState(1);
  // New modal state for link insertion.
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkCaption, setLinkCaption] = useState("");
  const [linkMode, setLinkMode] = useState("link");
  const [linkImage, setLinkImage] = useState("");
  const [linkError, setLinkError] = useState("");

  const editorContainerRef = useRef(null);
  const insertDropdownButtonRef = useRef(null);
  const alignDropdownButtonRef = useRef(null);
  const selectionLocation = useRef(null);
  const targetNodeRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const sensorRef = useRef({ armed: false });

  // New refs to avoid global IDs
  const editorRef = useRef(null);
  const blocksRef = useRef(null);
  const richRef = useRef(null);
  const textToolsRef = useRef(null);
  const callbacksRef = useRef({ setDocContent, onOpenLink });
  callbacksRef.current = { setDocContent, onOpenLink };
  const blockDropdownButtonRef = useRef(null);

  // Dropdown container refs.
  const blockDropdownRef = useRef(null);
  const alignDropdownRef = useRef(null);
  const insertDropdownRef = useRef(null);

  const pendingRemoteHTMLRef = useRef(null);

  // Update sanitizer to allow div and button.
  function utilizeSanitize(htmlString) {
    return sanitizeHtml(htmlString, {
      allowedTags: [
        "p",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "blockquote",
        "pre",
        "ul",
        "ol",
        "li",
        "a",
        "b",
        "i",
        "u",
        "s",
        "span",
        "img",
        "iframe",
        "input",
        "details",
        "summary",
        "table",
        "tr",
        "td",
        "div",
        "button",
        "br",
        "svg",
        "path",
        "strong",
        "em",
        "del",
        "strike",
        "code",
        "sub",
        "sup",
        "hr",
        "thead",
        "tbody",
        "tfoot",
        "th",
        "caption",
        "colgroup",
        "col",
      ],
      allowedAttributes: {
        a: ["href", "title", "target", "rel"],
        img: ["src", "alt"],
        iframe: ["src", "width", "height", "frameborder", "allowfullscreen"],
        input: ["type", "checked", "contenteditable", "aria-label"],
        span: ["contenteditable"],
        ol: ["start", "reversed", "type"],
        li: ["value"],
        td: ["colspan", "rowspan"],
        th: ["colspan", "rowspan", "scope"],
        col: ["span"],
        colgroup: ["span"],
        details: ["open"],
        table: ["border", "width", "style"],
        svg: ["xmlns", "width", "height", "viewBox", "fill"],
        path: ["d", "fill"],
        "div": ["contenteditable", "data-ant-kind", "data-ant-url"],
        "*": ["style", "class"],
      },
      allowedSchemes: ["http", "https", "mailto", "tel"],
      nonTextTags: [],
      exclusiveFilter: (frame) =>
        (frame.attribs.class || "").split(/\s+/).includes("anteditor-code-copy-btn"),
    });
  }

  // Global click handler for dropdown closure.
  useEffect(() => {
    const handleGlobalClick = (event) => {
      const blockButton = blockDropdownButtonRef.current;
      if (
        showBlockOptions &&
        blockDropdownRef.current &&
        blockButton &&
        !blockDropdownRef.current.contains(event.target) &&
        !blockButton.contains(event.target)
      ) {
        setShowBlockOptions(false);
      }
      if (
        showAlignOptions &&
        alignDropdownRef.current &&
        alignDropdownButtonRef.current &&
        !alignDropdownRef.current.contains(event.target) &&
        !alignDropdownButtonRef.current.contains(event.target)
      ) {
        setShowAlignOptions(false);
      }
      if (
        showInsertOptions &&
        insertDropdownRef.current &&
        insertDropdownButtonRef.current &&
        !insertDropdownRef.current.contains(event.target) &&
        !insertDropdownButtonRef.current.contains(event.target)
      ) {
        setShowInsertOptions(false);
      }
    };
    document.addEventListener("mousedown", handleGlobalClick);
    return () => document.removeEventListener("mousedown", handleGlobalClick);
  }, [showBlockOptions, showAlignOptions, showInsertOptions]);

  useEffect(() => {
    const editorElem = editorRef.current;
    if (!editorElem) return;
    const save = () => {
      if (!editorElem.isContentEditable) return;
      callbacksRef.current.setDocContent(utilizeSanitize(richRef.current?.html() || editorElem.innerHTML));
    };
    blocksRef.current = createAntBlocks(editorElem, save);
    textToolsRef.current = createAntTextTools(editorElem, {
      onChange: save, onIndent: (outdent) => blocksRef.current?.indent(outdent),
    });
    richRef.current = createAntRichContent(editorElem, {
      onChange: save,
      onContinueBelow: (block) => textToolsRef.current?.continueBelow(block),
      onLayout: () => { textToolsRef.current?.normalizeQuotes(); textToolsRef.current?.syncTheme(); },
      sanitize: utilizeSanitize,
      onOpenLink: (url) => callbacksRef.current.onOpenLink ? callbacksRef.current.onOpenLink(url) : window.open(url, "_blank", "noopener,noreferrer"),
      onRequestLink: ({ url, caption, mode, imageUrl }) => {
        setLinkUrl(url); setLinkCaption(caption); setLinkMode(mode);
        setLinkImage(imageUrl); setLinkError(""); setShowLinkModal(true);
      },
    });
    return () => {
      blocksRef.current?.destroy(); richRef.current?.destroy(); textToolsRef.current?.destroy();
      blocksRef.current = null; richRef.current = null;
    };
  }, []);

  // ⬇️ This is the one that used to be useEffect; now it's useLayoutEffect
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    const applySanitized = (html) => {
      const incoming = utilizeSanitize(html ?? "");
      const current = utilizeSanitize(richRef.current?.html() ?? el.innerHTML ?? "");
      if (incoming !== current) {
        el.innerHTML = incoming;
        blocksRef.current?.normalize();
        richRef.current?.decorate();
      }
    };

    const isFocused =
      document.activeElement === el || el.contains(document.activeElement);

    if (isFocused) {
      pendingRemoteHTMLRef.current = initialValue ?? "";
    } else {
      applySanitized(initialValue);
    }

    function onBlur() {
      if (pendingRemoteHTMLRef.current != null) {
        applySanitized(pendingRemoteHTMLRef.current);
        pendingRemoteHTMLRef.current = null;
      }
    }

    el.addEventListener("blur", onBlur);
    return () => el.removeEventListener("blur", onBlur);
  }, [initialValue]);

  useEffect(() => {
    const editorElem = editorRef.current;
    if (!editorElem) return;

    const handleFocus = () => {
      if (
        editorElem.innerHTML.trim() === "" ||
        editorElem.innerHTML === "<br>"
      ) {
        editorElem.innerHTML = "<p><br></p>";
        const newRange = document.createRange();
        newRange.setStart(editorElem.firstChild, 0);
        newRange.collapse(true);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(newRange);
      }
    };
    editorElem.addEventListener("focus", handleFocus);
    return () => editorElem.removeEventListener("focus", handleFocus);
  }, []);

  function createImageFromURL({ src, altText }) {
    const editorelement = editorRef.current;
    if (!editorelement || !src) return;
    const img = document.createElement("img");
    img.src = src;
    img.alt = altText;
    editorelement.append(img);
    callbacksRef.current.setDocContent(utilizeSanitize(richRef.current?.html() || editorelement.innerHTML));
    setShowLinkImgModal(false);
  }

  const onImageConfirmClick = async () => {
    if (imageUploadBusy.current) return;
    imageUploadBusy.current = true;
    setInsertingImage(true);
    const target = editorRef.current;
    try {
      const url = await handleUploadDocImage();
      if (!url || !target || editorRef.current !== target) return;
      createImageFromURL({ src: url, altText: docImgFileAltText });
      setShowFileImgModal(false);
    } catch (error) {
      window.alert(error?.message || "The image could not be uploaded. Please try again.");
    } finally {
      imageUploadBusy.current = false;
      setInsertingImage(false);
    }
  };

  function createCollapsible() {
    blocksRef.current?.insertDetails();
  }

  function toggleBold() {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const bold = document.createElement("b");
    const isBold =
      range.commonAncestorContainer.parentNode.tagName.toLowerCase() === "b";
    if (isBold) {
      const text = range.extractContents();
      range.commonAncestorContainer.parentNode.replaceWith(text);
      range.insertNode(text);
    } else {
      bold.appendChild(range.extractContents());
      range.insertNode(bold);
    }
  }

  function toggleItalic() {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const italic = document.createElement("i");
    const isItalic =
      range.commonAncestorContainer.parentNode.tagName.toLowerCase() === "i";
    if (isItalic) {
      const text = range.extractContents();
      range.commonAncestorContainer.parentNode.replaceWith(text);
      range.insertNode(text);
    } else {
      italic.appendChild(range.extractContents());
      range.insertNode(italic);
    }
  }

  function toggleUnderline() {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const underline = document.createElement("u");
    const isUnderline =
      range.commonAncestorContainer.parentNode.tagName.toLowerCase() === "u";
    if (isUnderline) {
      const text = range.extractContents();
      range.commonAncestorContainer.parentNode.replaceWith(text);
      range.insertNode(text);
    } else {
      underline.appendChild(range.extractContents());
      range.insertNode(underline);
    }
  }

  function toggleStrike() {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const strike = document.createElement("s");
    const isStrike =
      range.commonAncestorContainer.parentNode.tagName.toLowerCase() === "s";
    if (isStrike) {
      const text = range.extractContents();
      range.commonAncestorContainer.parentNode.replaceWith(text);
      range.insertNode(text);
    } else {
      strike.appendChild(range.extractContents());
      range.insertNode(strike);
    }
  }

  function handleUndo() {
    document.execCommand("undo", false, null);
  }

  function handleRedo() {
    document.execCommand("redo", false, null);
  }

  function handleAlignCommands(alignValue) {
    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    range.commonAncestorContainer.parentElement.style.textAlign = alignValue;
  }

  function handleChangeType(type) {
    if (type === "pre") richRef.current?.insertCode();
    else textToolsRef.current?.setType(type);
  }

  function handleIndentation(type) {
    if ((type === "indent" || type === "outdent") && blocksRef.current?.indent(type === "outdent")) return;
    document.execCommand(type, false, null);
  }

  function handleInsertList(type) {
    blocksRef.current?.toggleList(type === "insertUnorderedList" ? "ul" : "ol");
  }

  const handleInsertChecklist = () => blocksRef.current?.toggleList("todo");

  function handleLinkButtonClick() { richRef.current?.prepareLink("link"); }
  function insertLink(event) {
    event.preventDefault();
    const error = richRef.current?.insertLink({ url: linkUrl, caption: linkCaption, mode: linkMode, imageUrl: linkImage });
    if (error) setLinkError(error);
    else setShowLinkModal(false);
  }

  function handleUnlink() {
    document.execCommand("unlink", false, null);
  }

  function changeTextColor(color) {
    textToolsRef.current?.color("text", color); setShowTextColors(false);
  }
  function changeHighlightColor(color) {
    textToolsRef.current?.color("highlight", color); setShowHighlightColors(false);
  }

  function removeHighlightColor() { textToolsRef.current?.color("highlight", "default"); }
  function removeTextColor() { textToolsRef.current?.color("text", "default"); }

  // --- Emoji Insertion ---
  function onSelectEmoji(emoji) {
    const editorElem = editorRef.current;
    const selection = window.getSelection();

    // Restore the saved selection if available.
    if (savedSelectionRef.current) {
      selection.removeAllRanges();
      selection.addRange(savedSelectionRef.current);
    }

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const node = document.createTextNode(emoji.native);
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else if (editorElem) {
      editorElem.appendChild(document.createTextNode(emoji.native));
    }

    setEmIsOpen(false);
    savedSelectionRef.current = null; // Clear the saved selection
  }
  // --- End Emoji Insertion ---

  useEffect(() => {
    const editorElem = editorRef.current;
    if (!editorElem) return;
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (selection.toString().length > 0) {
        selectionLocation.current = selection
          .getRangeAt(0)
          .getBoundingClientRect();
        setShowFloatingToolbar(true);
      } else {
        setShowFloatingToolbar(false);
      }
    };
    editorElem.addEventListener("mouseup", handleSelectionChange);
    editorElem.addEventListener("keyup", handleSelectionChange);
    return () => {
      editorElem.removeEventListener("mouseup", handleSelectionChange);
      editorElem.removeEventListener("keyup", handleSelectionChange);
    };
  }, []);

  function capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }

  useEffect(() => {
    if (spokenText.length > 0) {
      const editorElem = editorRef.current;
      if (!editorElem) return;
      const p = document.createElement("p");
      p.textContent = spokenText;
      editorElem.appendChild(p);
      if (spokenText.toLowerCase().includes("in my tasks")) {
        let taskTitle = spokenText.trim().substring(4, spokenText.length - 62);
        const capitalizedTask = capitalize(taskTitle);
        createNewInboxTask(currentUser.uid, capitalizedTask, null);
        const utterance = new SpeechSynthesisUtterance(
          `Sure, I added ${taskTitle} to your tasks.`,
        );
        speechSynthesis.speak(utterance);
      }
    }
  }, [spokenText, currentUser?.uid]);

  // Function to insert table.
  function insertTable(rows, cols) {
    let tableHTML = `<table border="1" width="100%" style="border-collapse: collapse; margin:25px 0;">`;
    for (let i = 0; i < rows; i++) {
      tableHTML += "<tr>";
      for (let j = 0; j < cols; j++) {
        tableHTML +=
          "<td style='border: 1px solid black; padding: 5px 8px; height: 40px;'>&nbsp;</td>";
      }
      tableHTML += "</tr>";
    }
    tableHTML += "</table>";
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, tableHTML);
    setShowTableModal(false);
  }

  useEffect(() => {
    const onMove = (e) => {
      const el = editorContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const y = e.clientY - rect.top; // cursor distance from top of editor

      // Hysteresis: enter at 120px, leave at 160px
      if (!sensorRef.current.armed && y >= 0 && y <= 120) {
        sensorRef.current.armed = true;
        setShowToolbar(true);
      } else if (sensorRef.current.armed && (y < 0 || y > 160)) {
        sensorRef.current.armed = false;
        setShowToolbar(false);
      }
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <div className="anteditor" ref={editorContainerRef}
      onMouseDownCapture={(event) => {
        if (event.target.closest("button") && !event.target.closest(".editor_modal")) {
          event.preventDefault();
        }
      }}>
      <div
        className={`anteditor_container ${
          showToolbar && mainToolbarOn && editability ? "toolbar-visible" : ""
        }`}
      >
        {mainToolbarOn && (
          <div className="anteditor_toolbar-overlay">
            <div style={{ display: "flex" }} className="anteditor_toolbar">
              <button onClick={handleUndo}>
                <HiOutlineArrowUturnLeft />
              </button>
              <button onClick={handleRedo}>
                <HiOutlineArrowUturnRight />
              </button>
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => setShowBlockOptions(!showBlockOptions)}
                ref={blockDropdownButtonRef}
                className="drop-item"
              >
                <TfiParagraph />
                <TbChevronDown />
              </button>
              {showBlockOptions &&
                createPortal(
                  <div
                    ref={blockDropdownRef}
                    className="dropdown"
                    style={{
                      position: "absolute",
                      top: `${
                        blockDropdownButtonRef.current &&
                        editorContainerRef.current
                          ? blockDropdownButtonRef.current.getBoundingClientRect()
                              .top -
                            editorContainerRef.current.getBoundingClientRect()
                              .top +
                            80
                          : 80
                      }px`,
                      left: `${
                        blockDropdownButtonRef.current &&
                        editorContainerRef.current
                          ? blockDropdownButtonRef.current.getBoundingClientRect()
                              .left -
                            editorContainerRef.current.getBoundingClientRect()
                              .left -
                            62
                          : 0
                      }px`,
                    }}
                  >
                    <button
                      className="item"
                      onClick={() => {
                        handleChangeType("p");
                        setShowBlockOptions(false);
                      }}
                    >
                      <TfiParagraph /> Paragraph
                    </button>
                    <div className="divider"></div>
                    <button
                      className="item"
                      onClick={() => {
                        handleChangeType("h2");
                        setShowBlockOptions(false);
                      }}
                    >
                      <span className="icon heading-2" /> Heading 2
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleChangeType("h3");
                        setShowBlockOptions(false);
                      }}
                    >
                      <span className="icon heading-3" /> Heading 3
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleChangeType("h4");
                        setShowBlockOptions(false);
                      }}
                    >
                      <span className="icon heading-4" /> Heading 4
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleChangeType("h5");
                        setShowBlockOptions(false);
                      }}
                    >
                      <span className="icon heading-5" /> Heading 5
                    </button>
                    <div className="divider"></div>
                    <button
                      className="item"
                      onClick={() => {
                        handleInsertList("insertUnorderedList");
                        setShowBlockOptions(false);
                      }}
                    >
                      <TbList /> Bulletlist
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleInsertList("insertOrderedList");
                        setShowBlockOptions(false);
                      }}
                    >
                      <TbListNumbers /> Numberedlist
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleInsertChecklist();
                        setShowBlockOptions(false);
                      }}
                    >
                      <TbListCheck /> Checklist
                    </button>
                    <div className="divider"></div>
                    <button
                      className="item"
                      onClick={() => {
                        handleChangeType("blockquote");
                        setShowBlockOptions(false);
                      }}
                    >
                      <BsQuote /> Quote
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleChangeType("pre");
                        setShowBlockOptions(false);
                      }}
                    >
                      <HiOutlineCodeBracket /> Codeblock
                    </button>
                  </div>,
                  editorContainerRef.current || document.body,
                )}
              <button onClick={toggleBold}>
                <TbBold />
              </button>
              <button onClick={toggleItalic}>
                <TbItalic />
              </button>
              <button onClick={toggleStrike}>
                <TbStrikethrough />
              </button>
              <button onClick={toggleUnderline}>
                <TbUnderline />
              </button>
              <button onClick={() => setShowTextColors(!showTextColors)}>
                <BsPaintBucket />
              </button>
              {showTextColors && (
                <ColorPalette key="text" returnColor={changeTextColor} />
              )}
              <button
                onClick={() => setShowHighlightColors(!showHighlightColors)}
              >
                <AiOutlineHighlight />
              </button>
              {showHighlightColors && (
                <ColorPalette
                  key="highlight"
                  returnColor={changeHighlightColor}
                />
              )}
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  const sel = window.getSelection();
                  if (sel.rangeCount > 0) {
                    savedSelectionRef.current = sel.getRangeAt(0);
                  }
                  setEmIsOpen(!emisOpen);
                }}
                id="emojiBtn"
              >
                <BsEmojiSmile />
              </button>
              {emisOpen &&
                createPortal(
                  <div
                    style={{
                      position: "absolute",
                      top:
                        document
                          .getElementById("emojiBtn")
                          .getBoundingClientRect().top >
                        window.innerHeight / 2
                          ? `${
                              document
                                .getElementById("emojiBtn")
                                .getBoundingClientRect().top - 440
                            }px`
                          : `${
                              document
                                .getElementById("emojiBtn")
                                .getBoundingClientRect().top + 30
                            }px`,
                      left: `${
                        document
                          .getElementById("emojiBtn")
                          .getBoundingClientRect().left
                      }px`,
                      zIndex: "150",
                    }}
                  >
                    <EmojiPicker onEmojiSelect={onSelectEmoji} />
                  </div>,
                  document.body,
                )}
              <button aria-label="Insert link" onClick={handleLinkButtonClick}>
                <TfiLink />
              </button>
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => setShowAlignOptions(!showAlignOptions)}
                id="alignDropdown"
                ref={alignDropdownButtonRef}
                className="drop-item"
              >
                <TbAlignLeft />
                <TbChevronDown />
              </button>
              {showAlignOptions &&
                createPortal(
                  <div
                    ref={alignDropdownRef}
                    className="dropdown"
                    style={{
                      position: "absolute",
                      top: `${
                        alignDropdownButtonRef.current &&
                        editorContainerRef.current
                          ? alignDropdownButtonRef.current.getBoundingClientRect()
                              .top -
                            editorContainerRef.current.getBoundingClientRect()
                              .top +
                            80
                          : 80
                      }px`,
                      left: `${
                        alignDropdownButtonRef.current &&
                        editorContainerRef.current
                          ? alignDropdownButtonRef.current.getBoundingClientRect()
                              .left -
                            editorContainerRef.current.getBoundingClientRect()
                              .left -
                            62
                          : 0
                      }px`,
                    }}
                  >
                    <button
                      className="item"
                      onClick={() => {
                        handleAlignCommands("left");
                        setShowAlignOptions(false);
                      }}
                    >
                      <TbAlignLeft />
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleAlignCommands("center");
                        setShowAlignOptions(false);
                      }}
                    >
                      <TbAlignCenter />
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleAlignCommands("right");
                        setShowAlignOptions(false);
                      }}
                    >
                      <TbAlignRight />
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleAlignCommands("justify");
                        setShowAlignOptions(false);
                      }}
                    >
                      <TbAlignJustified />
                    </button>
                    <div className="divider"></div>
                    <button
                      className="item"
                      onClick={() => {
                        handleIndentation("indent");
                        setShowAlignOptions(false);
                      }}
                    >
                      <TbIndentIncrease />
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        handleIndentation("outdent");
                        setShowAlignOptions(false);
                      }}
                    >
                      <TbIndentDecrease />
                    </button>
                    <div className="divider"></div>
                    <button
                      className="item"
                      onClick={() => {
                        handleIndentation("insertHorizontalRule");
                        setShowAlignOptions(false);
                      }}
                    >
                      <BsFileBreak />
                    </button>
                  </div>,
                  editorContainerRef.current || document.body,
                )}
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => setShowInsertOptions(!showInsertOptions)}
                ref={insertDropdownButtonRef}
                className="drop-item"
              >
                <TbPlus />
                <TbChevronDown />
              </button>
              {showInsertOptions &&
                createPortal(
                  <div
                    ref={insertDropdownRef}
                    className="dropdown"
                    style={{
                      position: "absolute",
                      top: `${
                        insertDropdownButtonRef.current &&
                        editorContainerRef.current
                          ? insertDropdownButtonRef.current.getBoundingClientRect()
                              .top -
                            editorContainerRef.current.getBoundingClientRect()
                              .top +
                            80
                          : 80
                      }px`,
                      left: `${
                        insertDropdownButtonRef.current &&
                        editorContainerRef.current
                          ? insertDropdownButtonRef.current.getBoundingClientRect()
                              .left -
                            editorContainerRef.current.getBoundingClientRect()
                              .left -
                            62
                          : 0
                      }px`,
                    }}
                  >
                    <button
                      className="item"
                      onClick={() => {
                        setShowImageOptions(!showImageOptions);
                        setShowInsertOptions(false);
                      }}
                    >
                      <BsCardImage /> Image
                    </button>
                    <button className="item" onClick={() => { richRef.current?.prepareLink("card"); setShowInsertOptions(false); }}><TfiLink /> Link card</button>
                    <button
                      className="item"
                      onClick={() => {
                        richRef.current?.prepareLink("embed");
                        setShowInsertOptions(false);
                      }}
                    >
                      <BsPlayBtn /> Video
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        richRef.current?.prepareLink("embed");
                        setShowInsertOptions(false);
                      }}
                    >
                      <TfiLayoutMediaRightAlt /> Web embed
                    </button>
                    <div className="divider"></div>
                    <button
                      className="item"
                      onClick={() => {
                        handleChangeType("blockquote");
                        setShowInsertOptions(false);
                      }}
                    >
                      <BsQuote /> Quote
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        createCollapsible();
                        setShowInsertOptions(false);
                      }}
                    >
                      <TbSquareToggleHorizontal /> Collapsible
                    </button>
                    <button
                      className="item"
                      onClick={() => {
                        setShowTableModal(true);
                        setShowInsertOptions(false);
                      }}
                    >
                      <BsTable /> Table
                    </button>
                  </div>,
                  editorContainerRef.current || document.body,
                )}
            </div>
          </div>
        )}
        <div
          ref={(node) => {
            editorRef.current = node;
            targetNodeRef.current = node;
          }}
          contentEditable={editability}
          className="anteditor_inner"
          placeholder={placeholder}
          style={{ paddingTop: mainToolbarOn ? 45 : 10 }}
        />
      </div>
      {floatingToolbarOn &&
        showFloatingToolbar &&
        editability &&
        createPortal(
          <div
            className="anteditor_mini-toolbar"
            style={{
              top: `${selectionLocation?.current?.top - 85}px`,
              left: `${selectionLocation?.current?.left}px`,
            }}
          >
            <button onClick={toggleBold}>
              <TbBold />
            </button>
            <button onClick={toggleItalic}>
              <TbItalic />
            </button>
            <button onClick={() => handleChangeType("blockquote")}>
              <BsQuote />
            </button>
          </div>,
          document.body,
        )}
      <AntListens setSpokenText={setSpokenText} />

      {showLinkModal && (
        <div className="editor_modal">
          <div className="editor_modal-overlay" onClick={() => setShowLinkModal(false)} />
          <form className="editor_imagelink ant-link-dialog" role="dialog" aria-modal="true" aria-label="Insert or edit link" onSubmit={insertLink} onKeyDown={(e) => { if (e.key === "Escape") setShowLinkModal(false); }}>
            <label>Insert as<select value={linkMode} onChange={(e) => setLinkMode(e.target.value)}>
              <option value="link">Text link</option><option value="card">Link card</option><option value="embed">Web embed</option>
            </select></label>
            <label>URL<input autoFocus value={linkUrl} placeholder="https://example.com" onChange={(e) => setLinkUrl(e.target.value)} /></label>
            <label>Caption<input value={linkCaption} onChange={(e) => setLinkCaption(e.target.value)} placeholder="Selected text or a caption" /></label>
            {linkMode !== "link" && <label>Preview image URL (optional)<input value={linkImage} onChange={(e) => setLinkImage(e.target.value)} placeholder="https://example.com/image.jpg" /></label>}
            {linkMode === "embed" && <p>Click the card and choose Load embed. Some sites block embedding; Open always opens the original link. Mobile shows a card.</p>}
            {!!linkError && <p role="alert">{linkError}</p>}
            <div className="ant-link-dialog-actions"><button type="button" onClick={() => setShowLinkModal(false)}>Cancel</button><button type="submit">Apply</button></div>
          </form>
        </div>
      )}

      {/* Table Modal */}
      {showTableModal && (
        <div className="editor_modal">
          <div
            className="editor_modal-overlay"
            onClick={() => setShowTableModal(false)}
          ></div>
          <div
            className="editor_imagelink"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="number"
              placeholder="Rows"
              min="1"
              value={tableRows}
              onChange={(e) => setTableRows(e.target.value)}
            />
            <input
              type="number"
              placeholder="Columns"
              min="1"
              value={tableCols}
              onChange={(e) => setTableCols(e.target.value)}
            />
            <div
              className="table-preview"
              style={{
                margin: "8px 0",
                maxHeight: "150px",
                overflowY: "auto",
                border: "1px solid #ccc",
                padding: "4px",
                width: "100%",
                borderRadius: 5,
              }}
            >
              {tableRows && tableCols && (
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                  {Array.from({ length: parseInt(tableRows, 10) }).map(
                    (_, i) => (
                      <tr key={i}>
                        {Array.from({ length: parseInt(tableCols, 10) }).map(
                          (_, j) => (
                            <td
                              key={j}
                              style={{
                                border: "1px solid black",
                                padding: "4px",
                              }}
                            >
                              &nbsp;
                            </td>
                          ),
                        )}
                      </tr>
                    ),
                  )}
                </table>
              )}
            </div>
            <button
              onClick={() => {
                editorRef.current?.focus();
                insertTable(parseInt(tableRows, 10), parseInt(tableCols, 10));
                setTableRows("");
                setTableCols("");
              }}
            >
              Insert Table
            </button>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageOptions && (
        <div className="editor_modal">
          <div
            className="editor_modal-overlay"
            onClick={() => setShowImageOptions(false)}
          ></div>
          <div className="editor_imagelink">
            <button
              className="item"
              onClick={() =>
                setShowFileImgModal(!showFileImgModal) &
                setShowInsertOptions(false) &
                setShowLinkImgModal(false)
              }
            >
              Upload File
            </button>
            <button
              className="item"
              onClick={() =>
                setShowLinkImgModal(!showLinkImgModal) &
                setShowInsertOptions(false) &
                setShowFileImgModal(false)
              }
            >
              From URL
            </button>
          </div>
        </div>
      )}
      {showFileImgModal && (
        <div className="editor_modal">
          <div
            className="editor_modal-overlay"
            onClick={() => { if (!imageUploadBusy.current) setShowFileImgModal(false); }}
          ></div>
          <div
            className="editor_imagelink"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="file"
              id="image-upload"
              name="image-upload"
              disabled={insertingImage}
              accept="image/*"
              onChange={(e) => setDocImgFile(e.target.files[0])}
            />
            <input
              type="text"
              name="alttext"
              placeholder="Alt Text"
              onChange={(e) => setDocImgFileAltText(e.target.value)}
            />
            <button type="button" disabled={insertingImage} onClick={onImageConfirmClick}>
              {insertingImage ? "Uploading…" : "Insert Image"}
            </button>
          </div>
        </div>
      )}
      {showLinkImgModal && (
        <div className="editor_modal">
          <div
            className="editor_modal-overlay"
            onClick={() => setShowLinkImgModal(false)}
          ></div>
          <div
            className="editor_imagelink"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="url"
              name="imagelink"
              placeholder="Image Link"
              autoFocus
              onChange={(e) => setImgFromWebLink(e.target.value)}
            />
            <input
              type="text"
              name="alttext"
              placeholder="Alt Text"
              onChange={(e) => setDocImgFileAltText(e.target.value)}
            />
            <button
              onClick={() =>
                createImageFromURL({
                  src: imgFromWebLink,
                  altText: docImgFileAltText,
                })
              }
            >
              Insert Image
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
